import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Circle,
  Copy,
  CreditCard,
  FileQuestion,
  QrCode,
  XCircle,
} from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DetailHeader } from '@/ui/DetailHeader';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import DADOS_SIMULADOS from './dados-simulados.json';
import { RegistrarPagamentoModal } from './RegistrarPagamentoModal';
import {
  type Cobranca,
  type EtapaCobranca,
  construirEtapasCobranca,
  formatarDataBr,
  formatarDataHoraBr,
  rotuloTipoServico,
  SITUACAO_PARA_BADGE,
} from './tipos';

/**
 * `/operacao/financeiro/cobrancas/:id` — detalhe de uma cobrança DAMSP.
 * Sem item de menu próprio (ver `ROTAS_FORA_DO_MENU` em `mapa.ts`): alcançado
 * só a partir da linha na lista de Cobranças DAMSP.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. O cenário `vazio` e um `:id`
 * sem correspondência na fixture levam ao mesmo estado de "não encontrada".
 */

const ETAPA_ICONE: Record<EtapaCobranca['estado'], typeof CheckCircle2> = {
  concluida: CheckCircle2,
  atual: Circle,
  atencao: AlertTriangle,
  erro: XCircle,
  pendente: Circle,
};

const ETAPA_COR: Record<EtapaCobranca['estado'], string> = {
  concluida: 'text-success',
  atual: 'text-info',
  atencao: 'text-warning',
  erro: 'text-destructive',
  pendente: 'text-muted-foreground',
};

export function CobrancaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cenario = useCenarioDeTela();

  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [overrides, setOverrides] = useState<Partial<Cobranca>>({});
  const [pixCopiado, setPixCopiado] = useState(false);
  const [boletoCopiado, setBoletoCopiado] = useState(false);

  // Sem persistência real ainda: ao trocar de cobrança, esquece a edição local anterior.
  useEffect(() => {
    setOverrides({});
  }, [id]);

  const cobrancaBase = useMemo(() => {
    if (cenario !== 'sucesso') return undefined;
    return (DADOS_SIMULADOS as Cobranca[]).find((c) => c.id === id);
  }, [cenario, id]);

  const cobranca: Cobranca | undefined = cobrancaBase
    ? { ...cobrancaBase, ...overrides }
    : undefined;

  const registrarPagamento = (formaPagamento: 'PIX' | 'BOLETO') => {
    setOverrides((atual) => ({
      ...atual,
      situacao: 'PAGA',
      formaPagamento,
      pagoEm: new Date().toISOString(),
    }));
  };

  const cancelarCobranca = () => {
    setOverrides((atual) => ({
      ...atual,
      situacao: 'CANCELADA',
      canceladaEm: new Date().toISOString(),
    }));
  };

  const copiar = async (texto: string, marcar: (valor: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(texto);
      marcar(true);
      window.setTimeout(() => marcar(false), 2000);
    } catch {
      // Sem área de transferência disponível — o valor continua visível para copiar manualmente.
    }
  };

  const podePagar = cobranca?.situacao === 'PENDENTE' || cobranca?.situacao === 'VENCIDA';
  const podeCancelar = podePagar;
  const etapas = cobranca ? construirEtapasCobranca(cobranca) : [];

  return (
    <div className="space-y-4">
      <EstadoSimulado cenario={cenario} descricao="a cobrança">
        {!cobranca ? (
          <EmptyState
            icon={FileQuestion}
            title="Cobrança não encontrada"
            description="Verifique o endereço ou volte para a lista de cobranças DAMSP."
            actionLabel="Voltar para cobranças"
            onAction={() => navigate('/operacao/financeiro/cobrancas')}
          />
        ) : (
          <div className="space-y-4">
            <DetailHeader
              backTo="/operacao/financeiro/cobrancas"
              backLabel="Cobranças DAMSP"
              title={cobranca.numeroDamsp}
              subtitle={cobranca.credenciado}
              status={{ variant: SITUACAO_PARA_BADGE[cobranca.situacao], label: cobranca.situacao }}
              actions={
                podeCancelar ? (
                  <ConfirmDialog
                    trigger={
                      <Button variant="destructive" size="sm">
                        <Ban className="mr-2 h-4 w-4" aria-hidden="true" />
                        Cancelar cobrança
                      </Button>
                    }
                    title="Cancelar esta cobrança?"
                    description="A cobrança deixa de poder ser paga. Esta ação não pode ser desfeita."
                    confirmLabel="Cancelar cobrança"
                    cancelLabel="Manter cobrança"
                    variant="destructive"
                    onConfirm={cancelarCobranca}
                  />
                ) : undefined
              }
            />

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Progresso da cobrança</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-0">
                  {etapas.map((etapa, indice) => {
                    const Icone = ETAPA_ICONE[etapa.estado];
                    const ehUltima = indice === etapas.length - 1;
                    return (
                      <li key={etapa.titulo} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <Icone
                            className={cn('h-5 w-5 shrink-0', ETAPA_COR[etapa.estado])}
                            aria-hidden="true"
                          />
                          {!ehUltima && <div className="mt-1 w-px flex-1 bg-border" />}
                        </div>
                        <div className={cn('min-w-0', !ehUltima && 'pb-4')}>
                          <p className="text-sm font-medium text-foreground">{etapa.titulo}</p>
                          {etapa.data && (
                            <p className="text-xs text-muted-foreground">
                              {formatarDataHoraBr(etapa.data)}
                            </p>
                          )}
                          {etapa.descricao && (
                            <p className="text-xs text-muted-foreground">{etapa.descricao}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dados da cobrança</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Tipo de serviço</span>
                    <span className="text-right">{rotuloTipoServico(cobranca.tipoServico)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Valor</span>
                    <MoneyDisplay value={cobranca.valor} className="font-semibold" />
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Emitida em</span>
                    <span>{formatarDataBr(cobranca.emitidaEm)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Vencimento</span>
                    <span>{formatarDataBr(cobranca.vencimento)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Credenciado</span>
                    <span className="text-right">
                      {cobranca.credenciado}
                      {cobranca.credenciadoDocumento && (
                        <>
                          <br />
                          <span className="font-mono text-xs text-muted-foreground">
                            {cobranca.credenciadoDocumento}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  {cobranca.descricao && (
                    <div className="border-t border-border pt-2">
                      <p className="mb-1 text-xs text-muted-foreground">Descrição</p>
                      <p>{cobranca.descricao}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {podePagar && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CreditCard className="h-4 w-4" aria-hidden="true" /> Pagamento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {cobranca.pixCopiaECola && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <QrCode className="h-4 w-4" aria-hidden="true" /> PIX
                        </div>
                        <div className="rounded-lg bg-muted p-3">
                          <p className="break-all font-mono text-xs text-muted-foreground">
                            {cobranca.pixCopiaECola.slice(0, 80)}...
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mt-2"
                            onClick={() =>
                              cobranca.pixCopiaECola &&
                              copiar(cobranca.pixCopiaECola, setPixCopiado)
                            }
                          >
                            <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                            {pixCopiado ? 'Copiado' : 'Copiar código PIX'}
                          </Button>
                          <span className="sr-only" aria-live="polite">
                            {pixCopiado ? 'Código PIX copiado' : ''}
                          </span>
                        </div>
                      </div>
                    )}
                    {cobranca.linhaDigitavel && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Boleto</p>
                        <div className="rounded-lg bg-muted p-3">
                          <p className="break-all font-mono text-xs text-muted-foreground">
                            {cobranca.linhaDigitavel}
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mt-2"
                            onClick={() =>
                              cobranca.linhaDigitavel &&
                              copiar(cobranca.linhaDigitavel, setBoletoCopiado)
                            }
                          >
                            <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                            {boletoCopiado ? 'Copiado' : 'Copiar linha digitável'}
                          </Button>
                          <span className="sr-only" aria-live="polite">
                            {boletoCopiado ? 'Linha digitável copiada' : ''}
                          </span>
                        </div>
                      </div>
                    )}
                    <Button className="w-full" onClick={() => setPagamentoAberto(true)}>
                      Registrar pagamento
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </EstadoSimulado>

      {cobranca && (
        <RegistrarPagamentoModal
          open={pagamentoAberto}
          onOpenChange={setPagamentoAberto}
          cobranca={{ numeroDamsp: cobranca.numeroDamsp, valor: cobranca.valor }}
          onConfirmar={registrarPagamento}
        />
      )}
    </div>
  );
}

export default CobrancaDetalhePage;
